// Shared content + curated imagery for Home Pro Connect
import {
  Home, Layers, Bath, Hammer, Building2, Warehouse,
  ClipboardCheck, Link2, MessagesSquare, Clock, FileText, MapPin, Compass,
} from "lucide-react";

export const CITIES = [
  "Summerville", "Charleston", "North Charleston", "Goose Creek",
  "Mount Pleasant", "Ladson", "Moncks Corner", "West Ashley",
  "Georgetown", "Pawleys Island", "Murrells Inlet", "Myrtle Beach", "Conway",
];

export const IMAGES = {
  hero: "/images/general-contractor-reviewing-renovation-plans.jpg",
  charleston: "https://images.pexels.com/photos/6760999/pexels-photo-6760999.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  blueprint: "https://images.unsplash.com/photo-1762146828422-50a8bd416d3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxob21lJTIwY29uc3RydWN0aW9uJTIwYmx1ZXByaW50cyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODcyNDM4MDl8MA&ixlib=rb-4.1.0&q=85",
  kitchen: "https://images.unsplash.com/photo-1682888813913-e13f18692019?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBraXRjaGVuJTIwcmVtb2RlbHxlbnwwfHx8fDE3ODcyNDM4MTB8MA&ixlib=rb-4.1.0&q=85",
  bathroom: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxiYXRocm9vbSUyMHJlbm92YXRpb24lMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODcyNDM4MDl8MA&ixlib=rb-4.1.0&q=85",
  addition: "https://images.unsplash.com/photo-1591474200742-8e512e6f98f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBob21lJTIwYWRkaXRpb24lMjBjb25zdHJ1Y3Rpb24lMjBleHRlcmlvcnxlbnwwfHx8fDE3ODcyNDQ1ODN8MA&ixlib=rb-4.1.0&q=85",
  remodel: "https://images.unsplash.com/photo-1649083048770-82e8ffd80431?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxoaWdoJTIwZW5kJTIwaG9tZSUyMHJlbm92YXRpb24lMjBjb25zdHJ1Y3Rpb24lMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODcyNDYwNjJ8MA&ixlib=rb-4.1.0&q=85",
  exterior: "https://images.pexels.com/photos/28885519/pexels-photo-28885519.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  outdoor: "https://images.unsplash.com/photo-1715090576114-c07384af2069?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxvdXRkb29yJTIwbGl2aW5nJTIwcGF0aW8lMjBkZWNrJTIwYmFja3lhcmR8ZW58MHx8fHwxNzg3MjQ0NTgyfDA&ixlib=rb-4.1.0&q=85",
};

// Six stronger categories. `type` preselects the matching form project type.
export const SERVICES = [
  { key: "additions", title: "Home Additions", type: "Home Addition", desc: "Expand your home with new rooms, second stories, primary suites, in-law suites, and additional living space.", icon: Home, image: IMAGES.addition, span: "lg:col-span-7", tall: true },
  { key: "remodels", title: "Complete Home Remodels", type: "Complete Home Remodel", desc: "Transform multiple rooms, layouts, finishes, and major areas of your home.", icon: Layers, image: IMAGES.remodel, span: "lg:col-span-5", tall: true },
  { key: "kitchenbath", title: "Kitchen & Bathroom Remodels", type: "Kitchen & Bathroom Remodel", desc: "Update the spaces you use most with new layouts, cabinetry, fixtures, finishes, and functionality.", icon: Bath, image: IMAGES.kitchen, span: "lg:col-span-6" },
  { key: "demo", title: "Demolition & Structural Changes", type: "Demolition & Structural Changes", desc: "Prepare for renovation with selective demolition, wall removal, structural modifications, and layout changes.", icon: Hammer, image: IMAGES.exterior, span: "lg:col-span-6" },
  { key: "renovations", title: "Interior & Exterior Renovations", type: "Interior & Exterior Renovation", desc: "Improve your home's appearance, comfort, functionality, and long-term value.", icon: Building2, image: IMAGES.bathroom, span: "lg:col-span-5" },
  { key: "garage", title: "Garage Conversions & Outdoor Living", type: "Garage Conversion & Outdoor Living", desc: "Create functional living areas, porches, decks, patios, outdoor kitchens, and converted spaces.", icon: Warehouse, image: IMAGES.outdoor, span: "lg:col-span-7" },
];

export const PROJECT_TYPES = [
  "Home Addition",
  "Complete Home Remodel",
  "Kitchen & Bathroom Remodel",
  "Demolition & Structural Changes",
  "Interior & Exterior Renovation",
  "Garage Conversion & Outdoor Living",
  "Other Major Home Improvement",
];

export const BUDGETS = [
  "Under $25,000",
  "$25,000 – $50,000",
  "$50,000 – $100,000",
  "$100,000 – $250,000",
  "$250,000 – $500,000",
  "More than $500,000",
  "Not sure yet",
];

export const TIMELINES = [
  "As soon as possible",
  "Within 30 days",
  "Within 1–3 months",
  "Within 3–6 months",
  "More than 6 months",
  "Just researching",
];

export const CONTACT_METHODS = ["Phone", "Text", "Email"];

export const BEFORE_AFTER = [
  {
    title: "Coastal Kitchen Remodel",
    tag: "Kitchen Remodel",
    before: "https://static.prod-images.emergentagent.com/jobs/c4efb335-e61d-4920-8b20-ba1cdcac8395/images/50c0fbe019483071e62b131dd9dd3e076e50973d795ca86eabd06143f6befe5d.jpeg",
    after: "https://static.prod-images.emergentagent.com/jobs/c4efb335-e61d-4920-8b20-ba1cdcac8395/images/e8296d9d3c87891bf994614d457a7d4ff7b7d56dd7e80ba9425f5e96fcfe2865.jpeg",
  },
  {
    title: "Master Bathroom Renovation",
    tag: "Bathroom Remodel",
    before: "https://static.prod-images.emergentagent.com/jobs/c4efb335-e61d-4920-8b20-ba1cdcac8395/images/05ee9126509df540a48b6e6f6f8504342725c7bd9ef72366302a1a58183d0ca8.jpeg",
    after: "https://static.prod-images.emergentagent.com/jobs/c4efb335-e61d-4920-8b20-ba1cdcac8395/images/87a427c696a7d28c84ff55f05f93c6b1912ce95063e6f18b8c15909293ec8fa9.jpeg",
  },
  {
    title: "Open-Concept Living Addition",
    tag: "Home Addition",
    before: "https://static.prod-images.emergentagent.com/jobs/c4efb335-e61d-4920-8b20-ba1cdcac8395/images/f39caf0eb11226806fb894af2ee0c20702b690b7bf5b9bae49e27eb371e45b6f.jpeg",
    after: "https://static.prod-images.emergentagent.com/jobs/c4efb335-e61d-4920-8b20-ba1cdcac8395/images/0367185398006ba24d62082c948069b91d53db5137c126998ed63fd7743b7276.jpeg",
  },
  {
    title: "Backyard Outdoor Living",
    tag: "Outdoor Living",
    before: "https://static.prod-images.emergentagent.com/jobs/c4efb335-e61d-4920-8b20-ba1cdcac8395/images/350dafdbf369312bda1bddb5d97e98cc62dbe92e0657a8e1a0cedefbc29c885e.jpeg",
    after: "https://static.prod-images.emergentagent.com/jobs/c4efb335-e61d-4920-8b20-ba1cdcac8395/images/965f19992a6b81ae973dce9fdb5ce38885719d4a622253926e1f73e9d43fec08.jpeg",
  },
];

export const TRUST = [
  { title: "Clear Process", body: "Understand what happens at every step.", icon: ClipboardCheck },
  { title: "Project-Fit Connection", body: "Connections based on project type and service area.", icon: Link2 },
  { title: "Direct Communication", body: "Discuss your project directly with the contractor.", icon: MessagesSquare },
];

export const STEPS = [
  { num: "01", title: "Tell Us About Your Project", body: "Share the project type, location, budget range, timeline, and the vision you have in mind." },
  { num: "02", title: "We Review Your Request", body: "Home Pro Connect reviews the details to understand the type of contractor and services your project may require." },
  { num: "03", title: "Get Connected", body: "Speak directly with the contractor to discuss your project, availability, estimate, and potential next steps." },
];

export const BENEFITS = [
  { title: "Save Time Searching", body: "Spend less time calling companies that may not provide the service you need.", icon: Clock },
  { title: "Explain Your Project Once", body: "Provide the essential details through one simple request.", icon: FileText },
  { title: "Connect by Project Type", body: "Your request is reviewed based on the work you need and where the property is located.", icon: Link2 },
  { title: "Communicate Directly", body: "Discuss the estimate, schedule, scope, and project terms directly with the contractor.", icon: MessagesSquare },
  { title: "Start With Greater Clarity", body: "Understand the process and what information is needed before the introduction.", icon: Compass },
];

export const AREA_LOCATIONS = [
  "Summerville", "Charleston", "North Charleston", "Goose Creek",
  "Mount Pleasant", "Ladson", "Moncks Corner", "West Ashley",
  "Georgetown", "Pawleys Island", "Murrells Inlet", "Myrtle Beach", "Conway",
  "Surrounding Lowcountry & Grand Strand communities",
];

export const FAQS = [
  { q: "What is Home Pro Connect?", a: "Home Pro Connect is a homeowner-to-contractor connection service for residential construction and renovation projects." },
  { q: "What types of projects can I submit?", a: "Major projects like home additions, complete remodels, kitchen and bathroom renovations, demolition, and new home building — plus everyday home services like HVAC, plumbing, roofing, flooring, painting, drywall, fencing, landscaping, tree removal, and move-out cleanouts." },
  { q: "What's the difference between Major Projects and Home Services?", a: "Major projects are coordinated by a general contractor who manages every trade and permit. Home services are handled by a specialist for that specific job. Tell us what you need and we'll help find the right fit." },
  { q: "Who provides the estimate?", a: "The contractor provides their own project assessment, estimate, availability, agreement, and terms directly to the homeowner." },
  { q: "Who performs the construction work?", a: "The independent contractor hired by the homeowner performs the construction work. Home Pro Connect does not perform or supervise construction." },
  { q: "What if I do not know my exact budget?", a: "You can select 'Not sure yet' and provide the project details you currently know." },
  { q: "Can I submit photos or plans?", a: "Yes. Photos, drawings, inspiration images, and project plans can help communicate your goals." },
  { q: "What happens after I submit?", a: "Home Pro Connect reviews the information and contacts you regarding the potential next step in the connection process." },
];

// Simple analytics helper — sends to PostHog (loaded in index.html) and GA4 (gtag).
// GA4 event mapping: form submissions use GA4's recommended "generate_lead" event so it can be
// marked as a key event in GA4 (Admin → Events) for conversion tracking.
const GA4_EVENT_MAP = {
  form_submit_success: "generate_lead",
  form_step1_complete: "form_progress",
};

export const track = (event, props = {}) => {
  try {
    if (typeof window !== "undefined" && window.posthog) window.posthog.capture(event, props);
  } catch (_) { /* no-op */ }
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", GA4_EVENT_MAP[event] || event, props);
    }
  } catch (_) { /* no-op */ }
};

export { MapPin };
